/*
 * Required customer shipping option selector (Phase 1 + Phase 2).
 *
 * Wires the [data-ks-shipping-selector] block rendered by
 * snippets/ks-shipping-option-selector.liquid on product pages.
 *
 * Source-of-truth model:
 *  - Cart attribute "Shipping Option" is the cart-level source of truth and
 *    is written via routes.cart_update_url.
 *  - Line item property "Shipping Option" is added per-product via the
 *    <select name="properties[Shipping Option]"> inside the product form.
 *  - localStorage (ks_shipping_option) is convenience state that restores
 *    the customer's selection while they continue shopping.
 *
 * Phase 2 (this file):
 *  - Maintains a single $0 "shipping marker" line item in the cart that
 *    matches the current Shipping Option. window.KS_SHIPPING_MARKERS is
 *    rendered server-side by snippets/ks-shipping-markers-data.liquid and
 *    maps selector values to marker variants.
 *
 * Public API (used by assets/product-form.js):
 *   window.KSShippingOption.getSelectedOption(formOrRoot)
 *   window.KSShippingOption.validate(formOrRoot)
 *   window.KSShippingOption.syncCartAttribute(value)
 *   window.KSShippingOption.getMarkerForValue(value)
 *   window.KSShippingOption.syncShippingMarker(value)
 *
 * This script is loaded from snippets/product__assets.liquid with `defer`,
 * so it is parsed/evaluated before the deferred product-form.js (which only
 * loads on the first form submit).
 */
(function () {
    "use strict";

    var STORAGE_KEY = "ks_shipping_option";
    var ATTRIBUTE_KEY = "Shipping Option";
    var ESTIMATED_DATE_ATTRIBUTE_KEY = "Estimated Ship Date";
    var WRAPPER_SELECTOR = "[data-ks-shipping-selector]";
    var SELECT_SELECTOR = "[data-ks-shipping-select]";
    var ESTIMATE_SELECTOR = "[data-ks-shipping-estimate]";
    var ESTIMATE_VALUE_SELECTOR = "[data-ks-shipping-estimate-value]";
    var ESTIMATED_DATE_INPUT_SELECTOR = "[data-ks-estimated-ship-date-input]";
    var INVALID_CLASS = "ks-shipping-option--invalid";
    var PENDING_ATTR = "data-ks-shipping-pending";
    var LEGACY_ALIASES = {
        "Ship on next available ship day": "Ship NOW",
    };
    var HOLD_WEEKS = {
        "Ship NOW": 0,
        "Hold 1 week": 1,
        "Hold 2 weeks": 2,
        "Hold 3 weeks": 3,
    };

    function readStored() {
        try {
            return localStorage.getItem(STORAGE_KEY) || "";
        } catch (_) {
            return "";
        }
    }

    function writeStored(value) {
        try {
            if (value) {
                localStorage.setItem(STORAGE_KEY, value);
            } else {
                localStorage.removeItem(STORAGE_KEY);
            }
        } catch (_) {
            /* ignore quota / private mode */
        }
    }

    function asRoot(formOrRoot) {
        if (!formOrRoot) return document;
        if (formOrRoot instanceof Element || formOrRoot instanceof Document) {
            return formOrRoot;
        }
        return document;
    }

    function findWrapper(formOrRoot) {
        var root = asRoot(formOrRoot);
        if (root.matches && root.matches(WRAPPER_SELECTOR)) return root;
        return root.querySelector ? root.querySelector(WRAPPER_SELECTOR) : null;
    }

    function findSelect(formOrRoot) {
        var root = asRoot(formOrRoot);
        if (root.matches && root.matches(SELECT_SELECTOR)) return root;
        return root.querySelector ? root.querySelector(SELECT_SELECTOR) : null;
    }

    function findEstimate(formOrRoot) {
        var root = asRoot(formOrRoot);
        return root.querySelector ? root.querySelector(ESTIMATE_SELECTOR) : null;
    }

    function findEstimateValue(formOrRoot) {
        var root = asRoot(formOrRoot);
        return root.querySelector
            ? root.querySelector(ESTIMATE_VALUE_SELECTOR)
            : null;
    }

    function findEstimatedDateInput(formOrRoot) {
        var root = asRoot(formOrRoot);
        return root.querySelector
            ? root.querySelector(ESTIMATED_DATE_INPUT_SELECTOR)
            : null;
    }

    function findProductFormHost(node) {
        if (!node || !node.closest) return null;
        return node.closest("product-form");
    }

    function setPendingState(productFormHost, isPending) {
        if (!productFormHost) return;
        if (isPending) {
            productFormHost.setAttribute(PENDING_ATTR, "");
        } else {
            productFormHost.removeAttribute(PENDING_ATTR);
        }
    }

    function getSelectedOption(formOrRoot) {
        var select = findSelect(formOrRoot);
        if (!select) return "";
        return normalizeShippingOption((select.value || "").trim());
    }

    function normalizeShippingOption(value) {
        var raw = (value || "").trim();
        if (!raw) return "";
        return LEGACY_ALIASES[raw] || raw;
    }

    function getNextShipDate(baseDate) {
        var now = baseDate ? new Date(baseDate) : new Date();
        var day = now.getDay();
        var shipDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        // Sunday (0) and Wednesday-Saturday (3-6) ship next Monday.
        if (day === 0) shipDate.setDate(shipDate.getDate() + 1);
        else if (day >= 3) shipDate.setDate(shipDate.getDate() + (8 - day));
        return shipDate;
    }

    function formatShipDate(dateValue) {
        if (!(dateValue instanceof Date) || isNaN(dateValue.getTime())) return "";
        return dateValue.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
        });
    }

    function getEstimatedShipDateForOption(value, baseDate) {
        var normalized = normalizeShippingOption(value);
        if (!normalized || typeof HOLD_WEEKS[normalized] !== "number") return "";
        var nextShipDate = getNextShipDate(baseDate);
        var holdWeeks = HOLD_WEEKS[normalized];
        nextShipDate.setDate(nextShipDate.getDate() + holdWeeks * 7);
        return formatShipDate(nextShipDate);
    }

    function updateOptionLabels(select) {
        if (!select) return;
        for (var i = 0; i < select.options.length; i++) {
            var option = select.options[i];
            var optionValue = normalizeShippingOption(option.value);
            if (!optionValue || typeof HOLD_WEEKS[optionValue] !== "number") continue;
            var estDate = getEstimatedShipDateForOption(optionValue);
            option.textContent = optionValue + " - " + estDate;
        }
    }

    function updateEstimateUI(formOrRoot, normalizedValue) {
        var estimatedDate = getEstimatedShipDateForOption(normalizedValue);
        var estimateWrap = findEstimate(formOrRoot);
        var estimateValueEl = findEstimateValue(formOrRoot);
        var estimatedInput = findEstimatedDateInput(formOrRoot);

        if (estimatedInput) estimatedInput.value = estimatedDate;

        if (estimateWrap && estimateValueEl) {
            if (estimatedDate) {
                estimateValueEl.textContent = estimatedDate;
                estimateWrap.hidden = false;
            } else {
                estimateValueEl.textContent = "";
                estimateWrap.hidden = true;
            }
        }
    }

    function syncCartAttributes(optionValue, estimatedDate) {
        var body = { attributes: {} };
        body.attributes[ATTRIBUTE_KEY] = optionValue || "";
        body.attributes[ESTIMATED_DATE_ATTRIBUTE_KEY] = estimatedDate || "";
        var url = getCartUpdateUrl();
        return fetch(url, {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify(body),
        }).then(function (res) {
            if (!res.ok) {
                return Promise.reject(
                    new Error("cart/update failed: " + res.status)
                );
            }
            return res
                .clone()
                .json()
                .catch(function () {
                    return null;
                });
        });
    }

    function setSelectedOption(value, formOrRoot) {
        var select = findSelect(formOrRoot);
        if (!select) return;
        var normalized = normalizeShippingOption(value || "");
        select.value = normalized;
        writeStored(normalized);
        updateEstimateUI(findWrapper(select) || asRoot(formOrRoot), normalized);
        clearError(findWrapper(select));
        setPendingState(findProductFormHost(select), !normalized);
    }

    function showError(wrapper) {
        if (!wrapper) return;
        wrapper.classList.add(INVALID_CLASS);
        var error = wrapper.querySelector(".ks-shipping-option__error");
        if (error) error.hidden = false;
    }

    function clearError(wrapper) {
        if (!wrapper) return;
        wrapper.classList.remove(INVALID_CLASS);
        var error = wrapper.querySelector(".ks-shipping-option__error");
        if (error) error.hidden = true;
    }

    function validate(formOrRoot) {
        var wrapper = findWrapper(formOrRoot);
        // No selector in this scope means there is nothing to validate.
        if (!wrapper) return true;

        var select = wrapper.querySelector(SELECT_SELECTOR);
        var value = select ? (select.value || "").trim() : "";

        if (!value) {
            showError(wrapper);
            setPendingState(findProductFormHost(wrapper), true);
            if (select) {
                try {
                    select.focus({ preventScroll: true });
                } catch (_) {
                    select.focus();
                }
                if (typeof select.scrollIntoView === "function") {
                    try {
                        select.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    } catch (_) {
                        select.scrollIntoView();
                    }
                }
            }
            return false;
        }

        clearError(wrapper);
        setPendingState(findProductFormHost(wrapper), false);
        return true;
    }

    function getCartUpdateUrl() {
        var routes = window.routes || {};
        if (routes.cart_update_url) return routes.cart_update_url;
        var root =
            (window.Shopify &&
                window.Shopify.routes &&
                window.Shopify.routes.root) ||
            "/";
        return root.replace(/\/$/, "") + "/cart/update.js";
    }

    function syncCartAttribute(value) {
        var normalized = normalizeShippingOption(value);
        var estimatedDate = getEstimatedShipDateForOption(normalized);
        return syncCartAttributes(normalized, estimatedDate);
    }

    function onSelectChange(event) {
        var select = event.currentTarget;
        var wrapper = findWrapper(select);
        var host = findProductFormHost(select);
        var value = normalizeShippingOption((select.value || "").trim());
        var estimatedDate = getEstimatedShipDateForOption(value);
        select.value = value;

        writeStored(value);
        updateEstimateUI(wrapper || findWrapper(select), value);

        if (value) {
            clearError(wrapper);
            setPendingState(host, false);
            // Best-effort cart attribute sync so the cart-level source of
            // truth reflects the customer's latest choice even before they
            // add a product to cart. Failures are ignored; the on-submit
            // sync in product-form.js will retry.
            syncCartAttributes(value, estimatedDate).catch(function () {});
        } else {
            setPendingState(host, true);
        }
    }

    function bindShopPayBlocking(host, wrapper) {
        if (!host || !wrapper || host.__ksShippingShopPayBound) return;
        host.__ksShippingShopPayBound = true;

        host.addEventListener(
            "click",
            function (event) {
                var target = event.target;
                if (!target || !target.closest) return;
                var paymentButton = target.closest(".shopify-payment-button");
                if (!paymentButton) return;
                if (!host.contains(paymentButton)) return;

                var value = getSelectedOption(wrapper);
                if (!value) {
                    event.preventDefault();
                    event.stopPropagation();
                    validate(wrapper);
                }
            },
            true
        );
    }

    /* ===== Phase 2: shipping-marker product sync ============================
     *
     * Ensures the cart contains exactly one of the four $0 marker line items
     * that matches the customer's selected shipping option. Concurrent calls
     * (e.g. rapid Add to Cart presses) are serialized via _markerSyncChain so
     * we never race when reading /cart.js, removing wrong markers, and
     * adding the target marker.
     * ====================================================================== */

    var MARKER_PROPERTY_KEY = "_shipping_option_marker";
    var _markerSyncChain = Promise.resolve();

    function getMarkers() {
        var m = window.KS_SHIPPING_MARKERS;
        return m && typeof m === "object" ? m : {};
    }

    function getMarkerForValue(value) {
        var normalized = normalizeShippingOption(value);
        if (!normalized) return null;
        var markers = getMarkers();
        return markers[normalized] || null;
    }

    function getAllMarkerVariantIds() {
        var markers = getMarkers();
        var ids = [];
        Object.keys(markers).forEach(function (key) {
            var m = markers[key];
            if (m && m.variant_id) ids.push(Number(m.variant_id));
        });
        return ids;
    }

    function postJson(url, body) {
        return fetch(url, {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify(body),
        });
    }

    function getCartJsonUrl() {
        var root =
            (window.Shopify &&
                window.Shopify.routes &&
                window.Shopify.routes.root) ||
            "/";
        return root.replace(/\/$/, "") + "/cart.js";
    }

    function getCartAddUrl() {
        var routes = window.routes || {};
        if (routes.cart_add_url) return routes.cart_add_url;
        var root =
            (window.Shopify &&
                window.Shopify.routes &&
                window.Shopify.routes.root) ||
            "/";
        return root.replace(/\/$/, "") + "/cart/add.js";
    }

    function getCartChangeUrl() {
        var routes = window.routes || {};
        if (routes.cart_change_url) return routes.cart_change_url;
        var root =
            (window.Shopify &&
                window.Shopify.routes &&
                window.Shopify.routes.root) ||
            "/";
        return root.replace(/\/$/, "") + "/cart/change.js";
    }

    function fetchCartJson() {
        return fetch(getCartJsonUrl(), {
            method: "GET",
            credentials: "same-origin",
            headers: { Accept: "application/json" },
        }).then(function (r) {
            if (!r.ok) throw new Error("cart.js failed: " + r.status);
            return r.json();
        });
    }

    function _doSyncShippingMarker(value) {
        var target = getMarkerForValue(value);
        if (!target || !target.variant_id) {
            // No mapped marker (or marker product not published / sold out).
            // Phase 2 can't add anything, but we should still try to clean up
            // any stale markers left behind from a previous selection.
            target = null;
        }

        return fetchCartJson().then(function (cart) {
            var allMarkerIds = getAllMarkerVariantIds();
            if (allMarkerIds.length === 0) {
                return { ok: false, reason: "no-markers-configured" };
            }

            var existingMarkers = (cart.items || []).filter(function (item) {
                return allMarkerIds.indexOf(Number(item.variant_id)) !== -1;
            });

            var targetVariantId = target ? Number(target.variant_id) : null;
            var targetExisting = existingMarkers.find(function (item) {
                return (
                    targetVariantId !== null &&
                    Number(item.variant_id) === targetVariantId
                );
            });
            var wrongMarkers = existingMarkers.filter(function (item) {
                return Number(item.variant_id) !== targetVariantId;
            });

            // Step 1: remove wrong markers (sequentially, by line item key
            // so multiple variants line up safely).
            var removalChain = wrongMarkers.reduce(function (p, item) {
                return p.then(function () {
                    return postJson(getCartChangeUrl(), {
                        id: item.key,
                        quantity: 0,
                    }).catch(function () {
                        /* keep going even if one removal fails */
                    });
                });
            }, Promise.resolve());

            // Step 2: add the target marker if not already present.
            return removalChain.then(function () {
                if (!target) {
                    return {
                        ok: true,
                        added: false,
                        removed: wrongMarkers.length,
                    };
                }
                if (targetExisting && targetExisting.quantity >= 1) {
                    return {
                        ok: true,
                        added: false,
                        removed: wrongMarkers.length,
                        marker: target,
                    };
                }
                var addBody = {
                    id: targetVariantId,
                    quantity: 1,
                    properties: {},
                };
                // Private property (leading "_") so the marker line stays
                // visually clean in the cart UIs but stays distinguishable
                // for ShipStation / cart logic.
                addBody.properties[MARKER_PROPERTY_KEY] = "true";
                return postJson(getCartAddUrl(), addBody)
                    .then(function (r) {
                        if (!r.ok) {
                            return Promise.reject(
                                new Error(
                                    "cart/add marker failed: " + r.status
                                )
                            );
                        }
                        return {
                            ok: true,
                            added: true,
                            removed: wrongMarkers.length,
                            marker: target,
                        };
                    })
                    .catch(function (err) {
                        return { ok: false, error: String(err) };
                    });
            });
        });
    }

    /**
     * Ensures the cart contains exactly one of the configured marker
     * products matching the supplied selector value. Calls are serialized
     * to avoid races when triggered from rapid Add to Cart actions.
     *
     * @param {string} value Selector label, e.g. "Hold 2 weeks".
     * @returns {Promise<{ ok: boolean, added?: boolean, removed?: number, marker?: object, error?: string, reason?: string }>}
     */
    function syncShippingMarker(value) {
        var run = function () {
            try {
                return _doSyncShippingMarker(value);
            } catch (err) {
                return Promise.resolve({ ok: false, error: String(err) });
            }
        };
        _markerSyncChain = _markerSyncChain.then(run, run);
        return _markerSyncChain;
    }

    function init(root) {
        var scope = root || document;
        var wrappers = scope.querySelectorAll
            ? scope.querySelectorAll(WRAPPER_SELECTOR)
            : [];

        for (var i = 0; i < wrappers.length; i++) {
            var wrapper = wrappers[i];
            if (wrapper.__ksShippingInit) continue;
            wrapper.__ksShippingInit = true;

            var select = wrapper.querySelector(SELECT_SELECTOR);
            if (!select) continue;
            updateOptionLabels(select);

            // Restore prior selection from localStorage if it matches a
            // currently-rendered option. Falls back to the empty default.
            var stored = normalizeShippingOption(readStored());
            if (stored) {
                var hasMatch = false;
                for (var j = 0; j < select.options.length; j++) {
                    if (
                        normalizeShippingOption(select.options[j].value) ===
                        stored
                    ) {
                        hasMatch = true;
                        break;
                    }
                }
                if (hasMatch) {
                    select.value = stored;
                }
            }
            updateEstimateUI(wrapper, normalizeShippingOption(select.value || ""));

            select.addEventListener("change", onSelectChange);

            var host = findProductFormHost(wrapper);
            setPendingState(host, !((select.value || "").trim()));
            bindShopPayBlocking(host, wrapper);
        }
    }

    window.KSShippingOption = {
        STORAGE_KEY: STORAGE_KEY,
        ATTRIBUTE_KEY: ATTRIBUTE_KEY,
        ESTIMATED_DATE_ATTRIBUTE_KEY: ESTIMATED_DATE_ATTRIBUTE_KEY,
        MARKER_PROPERTY_KEY: MARKER_PROPERTY_KEY,
        normalizeShippingOption: normalizeShippingOption,
        getEstimatedShipDateForOption: getEstimatedShipDateForOption,
        getSelectedOption: getSelectedOption,
        setSelectedOption: setSelectedOption,
        validate: validate,
        syncCartAttribute: syncCartAttribute,
        getMarkerForValue: getMarkerForValue,
        syncShippingMarker: syncShippingMarker,
        init: init,
    };

    if (
        document.readyState === "complete" ||
        document.readyState === "interactive"
    ) {
        init(document);
    } else {
        document.addEventListener("DOMContentLoaded", function () {
            init(document);
        });
    }
})();
